import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface BillingAddress {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface NotificationPrefs {
  weekly_report: boolean;
  campaign_alerts: boolean;
  opportunities: boolean;
  os_category_summary: boolean;
}

export interface PartnerSettings {
  id: string;
  name: string;
  tier: string;
  legalName: string | null;
  cnpj: string | null;
  billingAddress: BillingAddress | null;
  billingEmail: string | null;
  notificationPrefs: NotificationPrefs;
  logoUrl: string | null;
}

interface UpdateSettingsPayload {
  legalName?: string;
  cnpj?: string;
  billingAddress?: BillingAddress;
  billingEmail?: string;
  notificationPrefs?: Partial<NotificationPrefs>;
}

export function usePartnerSettings() {
  return useQuery<PartnerSettings>({
    queryKey: ['partner-settings'],
    queryFn: async () => {
      const res = await api.get('/partners/my/settings');
      return res.data;
    },
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateSettingsPayload) => {
      const res = await api.patch('/partners/my/settings', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-settings'] });
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      // 1. Get presigned URL
      const presignRes = await api.post('/partners/my/settings/logo');
      const { uploadUrl, s3Key } = presignRes.data as { uploadUrl: string; s3Key: string };

      // 2. Upload directly to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'image/png' },
      });

      // 3. Confirm upload
      const confirmRes = await api.post('/partners/my/settings/logo/confirm', { s3Key });
      return confirmRes.data as { logoUrl: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-settings'] });
    },
  });
}
